import React, { useMemo } from 'react';
import { Button } from '@saleshandy/design-system';
import { Phone } from '@saleshandy/icons';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { V2Lead } from '../../../types';
import PhoneListWithCopy from '../../profile-modal/shared/phone-list-with-copy';
import { LeadRevealFeedbackSentiment } from '../../../helpers/leads.api';

interface PhoneCellProps {
  lead: V2Lead;
  onRevealPhone: (leadId: number) => void;
  isRevealing?: boolean;
  isAnyRevealing?: boolean;
  isAlreadyRevealed?: boolean;
  defaultEmailRevealType?: string;
  feedbackSentiment?: 'positive' | 'negative';
  feedbackMessage?: string;
  onFeedbackSubmitted?: (
    sentiment: LeadRevealFeedbackSentiment,
    message?: string,
  ) => void;
}

const PhoneCell: React.FC<PhoneCellProps> = ({
  lead,
  onRevealPhone,
  isRevealing = false,
  isAnyRevealing = false,
  isAlreadyRevealed = false,
  defaultEmailRevealType = 'work_email',
  feedbackSentiment,
  feedbackMessage,
  onFeedbackSubmitted,
}) => {
  const { phones, hasPhones } = useMemo(() => {
    const phonesData = lead?.phones || [];
    const extractedPhones = phonesData
      .map((phoneObj: any) =>
        typeof phoneObj === 'string' ? phoneObj : phoneObj?.number || '',
      )
      .filter(Boolean);
    return {
      phones: extractedPhones,
      hasPhones: extractedPhones.length > 0,
    };
  }, [lead.phones]);

  // Has data → show with inline feedback visible on CSS :hover
  if (hasPhones) {
    return (
      <PhoneListWithCopy
        phones={phones}
        feedbackProps={{
          leadId: lead?.id,
          initialSentiment: feedbackSentiment,
          initialMessage: feedbackMessage,
          onFeedbackSubmitted,
        }}
      />
    );
  }

  // Currently enriching (check before isAlreadyRevealed since a re-reveal may be in progress)
  if (isRevealing) {
    return (
      <div className="v2-lead-phone-cell">
        <OverlayTooltip placement={Placement.Bottom} text="Enriching...">
          <Button variant="secondary" disabled className="v2-lead-reveal-btn">
            <Phone width={16} height={16} />
            <span className="revealing-text">
              Enriching
              <div className="loader" />
            </span>
          </Button>
        </OverlayTooltip>
      </div>
    );
  }

  // Already revealed for this type but no data → not found
  if (isAlreadyRevealed) {
    return <div className="v2-lead-phone-cell">-</div>;
  }

  // Not revealed yet → show reveal button
  return (
    <div className="v2-lead-phone-cell">
      <OverlayTooltip
        placement={Placement.Bottom}
        text={
          isAnyRevealing
            ? 'Enriching...'
            : defaultEmailRevealType === 'personal_email'
            ? 'View Email + Phone: 9 credits required'
            : 'View Email + Phone: 7 credits required'
        }
        className="view-email-phone-info-button-tooltip"
      >
        <Button
          variant="secondary"
          onClick={() => onRevealPhone(lead?.id)}
          disabled={isAnyRevealing}
          className="v2-lead-reveal-btn"
        >
          <Phone width={16} height={16} />
          View Phone
        </Button>
      </OverlayTooltip>
    </div>
  );
};

export default React.memo(
  PhoneCell,
  (prevProps, nextProps) =>
    prevProps.lead?.id === nextProps.lead?.id &&
    JSON.stringify(prevProps.lead?.phones) ===
      JSON.stringify(nextProps.lead?.phones) &&
    prevProps.isRevealing === nextProps.isRevealing &&
    prevProps.isAnyRevealing === nextProps.isAnyRevealing &&
    prevProps.isAlreadyRevealed === nextProps.isAlreadyRevealed &&
    prevProps.onRevealPhone === nextProps.onRevealPhone &&
    prevProps.defaultEmailRevealType === nextProps.defaultEmailRevealType &&
    prevProps.feedbackSentiment === nextProps.feedbackSentiment &&
    prevProps.feedbackMessage === nextProps.feedbackMessage &&
    prevProps.onFeedbackSubmitted === nextProps.onFeedbackSubmitted,
);
